import { Injectable } from '@angular/core'

@Injectable({
  providedIn: 'root'
})
export class DateFormattingService {

  /**
   * Formats a date to show relative time for recent dates, absolute dates for older ones
   * @param date The date to format (can be Date, string, or null)
   * @param fallbackText Text to show when date is null/invalid (default: 'Never')
   * @returns Formatted date string
   */
  getRelativeTimeDisplay(date: Date | string | null, fallbackText: string = 'Never'): string {
    if (!date) return fallbackText

    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) return fallbackText

    const now = new Date()
    const diffMs = now.getTime() - dateObj.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return '1 day ago'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
    }

    return dateObj.toLocaleDateString()
  }

  /**
   * Gets a detailed tooltip for relative dates showing full date and time
   * @param date The date to format (can be Date, string, or null)
   * @returns Formatted tooltip string or null if not applicable
   */
  getRelativeTimeTooltip(date: Date | string | null): string | null {
    if (!date) return null

    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) return null

    // Only show tooltip for relative dates (not "Never" or already formatted dates)
    const now = new Date()
    const diffMs = now.getTime() - dateObj.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 30) {
      // Show full date and time for relative dates (without seconds)
      return dateObj.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
    }

    return null
  }
}